import {
  PASSWORD_MIN_LENGTH,
  acceptInvitationRequestSchema,
  singleUseTokenSchema,
  type AcceptInvitationRequest,
  type CurrentUser,
  type InvitationPreview,
} from '@mfos/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SubmitEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErrorNotice, LoadingNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { PublicLayout } from '../../components/PublicLayout';
import { formatDateTime, humanize } from '../../lib/format';
import { loginPathFor } from '../../lib/safe-next';
import { validate, type FieldErrors } from '../../lib/validation';
import { authKeys, useLogout, useOptionalCurrentUser } from '../auth/auth.api';
import { useFragmentToken } from '../auth/use-fragment-token';
import { acceptInvitation, useInvitationPreview } from './invitations.api';
import { phrase } from '../../lib/i18n/runtime';

export function InvitePage() {
  const fragmentToken = useFragmentToken();
  const token =
    fragmentToken !== null && singleUseTokenSchema.safeParse(fragmentToken).success
      ? fragmentToken
      : null;
  const preview = useInvitationPreview(token);
  const session = useOptionalCurrentUser();

  return (
    <PublicLayout title={phrase('Join a workspace')}>
      {token === null && (
        <p>
          {phrase(
            'This invitation link is incomplete or invalid. Open the link from your invitation email again, or ask the person who invited you to resend it.',
          )}
        </p>
      )}
      {token !== null && (preview.isPending || session.isPending) && (
        <LoadingNotice>{phrase('Checking your invitation…')}</LoadingNotice>
      )}
      {preview.isError && (
        <>
          <ErrorNotice error={preview.error} />
          <p>{phrase('Ask the person who invited you to send a new invitation.')}</p>
        </>
      )}
      {token !== null && preview.isSuccess && session.isSuccess && (
        <>
          <InvitationSummary preview={preview.data} />
          <AcceptSection token={token} preview={preview.data} user={session.data} />
        </>
      )}
    </PublicLayout>
  );
}

function InvitationSummary({ preview }: { preview: InvitationPreview }) {
  return (
    <dl className="summary-list">
      <div>
        <dt>{phrase('Workspace')}</dt>
        <dd>{preview.workspaceName}</dd>
      </div>
      <div>
        <dt>{phrase('Invited email')}</dt>
        <dd>{preview.email}</dd>
      </div>
      <div>
        <dt>{phrase('Role')}</dt>
        <dd>
          {humanize(preview.role)}
          {preview.title !== null ? ` · ${preview.title}` : ''}
        </dd>
      </div>
      <div>
        <dt>{phrase('Invited by')}</dt>
        <dd>{preview.invitedBy}</dd>
      </div>
      <div>
        <dt>{phrase('Expires')}</dt>
        <dd>
          <time dateTime={preview.expiresAt}>{formatDateTime(preview.expiresAt)}</time>
        </dd>
      </div>
    </dl>
  );
}

function AcceptSection({
  token,
  preview,
  user,
}: {
  token: string;
  preview: InvitationPreview;
  user: CurrentUser | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useLogout();
  const mutation = useMutation({
    mutationFn: (body: AcceptInvitationRequest) => acceptInvitation(body),
    onSuccess: async (result) => {
      if (!result.signedIn && user === null) {
        void navigate(loginPathFor(`/w/${result.workspaceId}`), { replace: true });
        return;
      }
      await queryClient.invalidateQueries();
      void navigate(`/w/${result.workspaceId}`, { replace: true });
    },
  });
  const invitePath = `/invite#token=${token}`;

  function signOut() {
    logout.mutate(undefined, {
      onSuccess: () => {
        queryClient.setQueryData(authKeys.optionalMe, null);
      },
    });
  }

  if (!preview.accountExists) {
    if (user !== null) {
      return (
        <WrongAccount
          user={user}
          preview={preview}
          onSignOut={signOut}
          signingOut={logout.isPending}
        />
      );
    }
    return <NewAccountForm token={token} mutation={mutation} />;
  }

  if (user === null) {
    return (
      <div className="stack">
        <p>
          An account already exists for {preview.email}. Sign in with that account to accept the
          invitation.
        </p>
        <Link className="button" to={loginPathFor(invitePath)}>
          {phrase('Sign in to accept')}
        </Link>
      </div>
    );
  }

  if (user.email.toLowerCase() !== preview.email.toLowerCase()) {
    return (
      <WrongAccount
        user={user}
        preview={preview}
        onSignOut={signOut}
        signingOut={logout.isPending}
      />
    );
  }

  return (
    <div className="stack">
      <p>You are signed in as {user.email}.</p>
      <ErrorNotice error={mutation.error} />
      <button
        type="button"
        className="button"
        disabled={mutation.isPending}
        onClick={() => {
          mutation.mutate({ token });
        }}
      >
        {mutation.isPending ? phrase('Joining…') : `Accept and join ${preview.workspaceName}`}
      </button>
    </div>
  );
}

function WrongAccount({
  user,
  preview,
  onSignOut,
  signingOut,
}: {
  user: CurrentUser;
  preview: InvitationPreview;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  return (
    <div className="stack">
      <p className="notice notice--warn" role="status">
        <span aria-hidden="true">! </span>
        You are signed in as {user.email}, but this invitation is for {preview.email}. Sign out,
        then open the invitation link again.
      </p>
      <button
        type="button"
        className="button button--secondary"
        onClick={onSignOut}
        disabled={signingOut}
      >
        {signingOut ? phrase('Signing out…') : phrase('Sign out')}
      </button>
    </div>
  );
}

function NewAccountForm({
  token,
  mutation,
}: {
  token: string;
  mutation: {
    mutate: (body: AcceptInvitationRequest) => void;
    isPending: boolean;
    error: unknown;
  };
}) {
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(acceptInvitationRequestSchema, { token, displayName, password });
    const nextErrors: Record<string, string> = result.ok ? {} : { ...result.errors };
    if (displayName.trim() === '') {
      nextErrors.displayName = 'Enter your name.';
    }
    if (confirmation !== password) {
      nextErrors.confirmation = 'The passwords do not match.';
    }
    setErrors(nextErrors);
    if (result.ok && Object.keys(nextErrors).length === 0) {
      mutation.mutate(result.data);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="stack">
      <h2>{phrase('Create your account')}</h2>
      <TextField
        label={phrase('Your name')}
        autoComplete="name"
        value={displayName}
        onValueChange={setDisplayName}
        error={errors.displayName}
        required
      />
      <TextField
        label={phrase('Password')}
        type="password"
        autoComplete="new-password"
        value={password}
        onValueChange={setPassword}
        error={errors.password}
        hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters. A long passphrase works well.`}
        required
      />
      <TextField
        label={phrase('Confirm password')}
        type="password"
        autoComplete="new-password"
        value={confirmation}
        onValueChange={setConfirmation}
        error={errors.confirmation}
        required
      />
      <ErrorNotice error={mutation.error} />
      <button type="submit" className="button" disabled={mutation.isPending}>
        {mutation.isPending ? phrase('Creating account…') : phrase('Create account and join')}
      </button>
    </form>
  );
}
